import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, Package, TrendingDown, AlertCircle, Truck, ShoppingCart, CheckCircle, Barcode, Copy, Sparkles, Loader2, Warehouse } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FichaProcurement, PriceStrip } from '@/components/procurement/FichaProcurement';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission } = useContext(AuthContext);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fichaPrices, setFichaPrices] = useState(null);
  const [savingProcurement, setSavingProcurement] = useState(false);
  const [procurementForm, setProcurementForm] = useState({});
  const [managedCategories, setManagedCategories] = useState(null);  // Set de categorias geridas
  const [categoryNames, setCategoryNames] = useState({});  // code -> descrição SAGE
  const [savingOverride, setSavingOverride] = useState(false);
  
  // Permission checks for Procurement Intelligence section
  const canEditProcurement = hasAnyPermission(['procurement.governance', 'procurement.ai_toggle']);
  const canEditInventory = hasPermission('inventory.edit');

  useEffect(() => {
    fetchProduct();
    fetchCategorySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const fetchCategorySettings = async () => {
    try {
      const res = await axios.get(`${API}/category-settings`);
      setManagedCategories(new Set(res.data.categories.filter(c => c.managed).map(c => c.category)));
      const names = {};
      res.data.categories.forEach(c => { if (c.description) names[c.category] = c.description; });
      setCategoryNames(names);
    } catch (e) {
      console.debug('category settings load failed:', e);
    }
  };

  const handleStockManagedOverride = async (managed) => {
    setSavingOverride(true);
    try {
      await axios.put(`${API}/products/${productId}/stock-managed`, { managed });
      setProduct(prev => ({ ...prev, stock_managed_override: managed ? true : undefined }));
      toast.success(managed
        ? 'Artigo passa a ser gerido na APP (override da categoria)'
        : 'Override removido — artigo segue a definição da categoria');
    } catch (e) {
      toast.error('Erro ao actualizar gestão de stock');
    } finally {
      setSavingOverride(false);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}?enriched=true`);
      setProduct(response.data);
      setProcurementForm({
        ai_tracking_enabled: !!response.data.ai_tracking_enabled,
        excluded_from_procurement: !!response.data.excluded_from_procurement,
        allow_forecast: response.data.allow_forecast !== false,
        allow_alerts: response.data.allow_alerts !== false,
        on_demand: !!response.data.on_demand,
        seasonal: !!response.data.seasonal,
        operational_priority: response.data.operational_priority || '',
        preferred_supplier_code: response.data.preferred_supplier_code || '',
        manual_reorder_point: response.data.manual_reorder_point ?? '',
        manual_max_stock: response.data.manual_max_stock ?? '',
        manual_safety_stock: response.data.manual_safety_stock ?? '',
        manual_moq: response.data.manual_moq ?? '',
        procurement_notes: response.data.procurement_notes || '',
      });
    } catch (error) {
      toast.error('Falha ao carregar produto');
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const toNumOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const handleSaveProcurement = async () => {
    setSavingProcurement(true);
    try {
      const payload = {
        ai_tracking_enabled: procurementForm.ai_tracking_enabled,
        excluded_from_procurement: procurementForm.excluded_from_procurement,
        allow_forecast: procurementForm.allow_forecast,
        allow_alerts: procurementForm.allow_alerts,
        on_demand: procurementForm.on_demand,
        seasonal: procurementForm.seasonal,
        operational_priority: procurementForm.operational_priority || null,
        preferred_supplier_code: procurementForm.preferred_supplier_code || null,
        manual_reorder_point: toNumOrNull(procurementForm.manual_reorder_point),
        manual_max_stock: toNumOrNull(procurementForm.manual_max_stock),
        manual_safety_stock: toNumOrNull(procurementForm.manual_safety_stock),
        manual_moq: toNumOrNull(procurementForm.manual_moq),
        procurement_notes: procurementForm.procurement_notes || null,
      };
      await axios.patch(`${API}/products/${productId}/procurement-config`, payload);
      toast.success('Configuração de procurement guardada');
      fetchProduct();
    } catch (err) {
      toast.error('Falha ao guardar configuração de procurement');
    } finally {
      setSavingProcurement(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  // Manual override wins over the AI value (resolution chain).
  const effectiveReorder = product.manual_reorder_point ?? product.reorder_point;
  
  // Stock by warehouse breakdown
  const stockByWarehouse = product.stock_by_warehouse || {};
  const totalAllWarehouses = Object.values(stockByWarehouse).reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);
  
  // IMPORTANT: Central warehouse (001) is what Procurement Intelligence uses
  // This is configurable globally via central_procurement_warehouse setting
  // User's reference_warehouse is for operational context, NOT procurement
  const CENTRAL_WAREHOUSE = "001"; // Matches backend central_procurement_warehouse
  const centralStock = parseInt(stockByWarehouse[CENTRAL_WAREHOUSE] || 0) || (product.current_stock || 0);
  
  // Display stock: Show central warehouse stock for procurement consistency
  // The "Existências por Armazém" section shows the full breakdown
  const displayStock = centralStock;
  
  const stockPercentage = effectiveReorder
    ? (displayStock / (effectiveReorder * 2)) * 100
    : 0;
  const isLowStock = effectiveReorder != null && displayStock <= effectiveReorder;
  const isOutOfStock = displayStock === 0;
  
  // Virtual stock calculations
  const pendingQuantity = product.pending_quantity || 0;
  const cartQuantity = product.cart_quantity || 0;
  const virtualStock = product.virtual_stock ?? product.current_stock;
  const hasPendingOrders = pendingQuantity > 0 || cartQuantity > 0;
  const isVirtualOk = effectiveReorder != null && virtualStock >= effectiveReorder;
  const pendingOrders = product.pending_orders || [];

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto" data-testid="product-detail-page">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          data-testid="back-to-inventory"
          onClick={() => navigate('/inventory')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao Inventário
        </Button>
        <h1 className="text-4xl font-bold tracking-tight">{product.name}</h1>
        <p className="text-muted-foreground mt-1">SKU: {product.sku}</p>
        {/* EAN/Barcode Info */}
        {(product.ean || (product.barcodes && product.barcodes.length > 0)) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Barcode className="h-4 w-4 text-slate-500" />
            {product.ean && (
              <Badge 
                variant="outline" 
                className="font-mono text-xs cursor-pointer hover:bg-slate-100"
                onClick={() => {
                  navigator.clipboard.writeText(product.ean);
                  toast.success('EAN copiado!');
                }}
                title="Clique para copiar"
              >
                EAN: {product.ean}
                <Copy className="h-3 w-3 ml-1" />
              </Badge>
            )}
            {product.barcodes?.map((barcode, idx) => (
              <Badge 
                key={idx}
                variant="secondary" 
                className="font-mono text-xs cursor-pointer hover:bg-slate-200"
                onClick={() => {
                  navigator.clipboard.writeText(barcode);
                  toast.success('Código de barras copiado!');
                }}
                title="Clique para copiar"
              >
                {barcode}
                <Copy className="h-3 w-3 ml-1" />
              </Badge>
            ))}
          </div>
        )}
        {/* Gestão de stock na APP (categoria + override) */}
        {managedCategories !== null && (() => {
          const catKey = product.category || '__sem_categoria__';
          const catDesc = categoryNames[catKey];
          const catLabel = product.category
            ? `${product.category}${catDesc ? ` — ${catDesc}` : ''}`
            : 'Sem categoria';
          const categoryManaged = managedCategories.has(catKey);
          const hasOverride = product.stock_managed_override === true;
          const effective = categoryManaged || hasOverride;
          return (
            <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="stock-managed-section">
              <Badge
                variant="outline"
                className={effective ? 'bg-cyan-50 text-cyan-700 border-cyan-300' : 'bg-slate-100 text-slate-500 border-slate-300'}
                data-testid="stock-managed-badge"
              >
                {effective ? 'Stock gerido na APP' : 'Fora de gestão (drop-shipping)'}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Categoria {catLabel}: {categoryManaged ? 'gerida' : 'não gerida'}
              </span>
              {!categoryManaged && canEditInventory && (
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Switch
                    checked={hasOverride}
                    disabled={savingOverride}
                    onCheckedChange={handleStockManagedOverride}
                    data-testid="stock-managed-override-switch"
                  />
                  Gerir stock deste artigo na APP
                </label>
              )}
            </div>
          );
        })()}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Cards */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Stock Atual (Central 001) */}
          <Card className="border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Stock Central (001)</CardTitle>
              <Package className="h-5 w-5 text-red-700" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold font-mono ${displayStock <= 0 ? 'text-red-600' : ''}`}>
                {displayStock}
              </div>
              <div className="mt-2 w-full bg-slate-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    isOutOfStock ? 'bg-red-500' : isLowStock ? 'bg-orange-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(stockPercentage, 100)}%` }}
                />
              </div>
              {totalAllWarehouses > displayStock && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Total rede: {totalAllWarehouses}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Pendente */}
          <Card className={`border rounded-sm shadow-none ${hasPendingOrders ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200'}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pendente</CardTitle>
              <Truck className={`h-5 w-5 ${hasPendingOrders ? 'text-blue-600' : 'text-slate-400'}`} strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <div className="flex flex-col">
                {hasPendingOrders ? (
                  <>
                    <div className="flex items-center gap-2">
                      {pendingQuantity > 0 && (
                        <span className="text-2xl font-bold font-mono text-blue-600">+{pendingQuantity}</span>
                      )}
                      {cartQuantity > 0 && (
                        <span className="flex items-center gap-1 text-lg font-mono text-purple-600">
                          <ShoppingCart className="h-4 w-4" />
                          +{cartQuantity}
                        </span>
                      )}
                    </div>
                    {pendingOrders.length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                        {pendingOrders.map((po, idx) => {
                          const remaining = po.remaining ?? (po.quantity - (po.received_quantity || 0));
                          return (
                            <div key={po.po_number || idx} className="flex items-center gap-1">
                              <span className="font-mono">{po.po_number}</span>
                              <span className="text-blue-600">({remaining} un.)</span>
                              {po.sage_synced && (
                                <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1 rounded">SAGE</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-2xl font-bold font-mono text-slate-400">-</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Stock Virtual */}
          <Card className={`border rounded-sm shadow-none ${
            isVirtualOk ? 'border-emerald-200 bg-emerald-50/30' : 
            virtualStock > 0 ? 'border-orange-200 bg-orange-50/30' : 
            'border-red-200 bg-red-50/30'
          }`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Stock Virtual</CardTitle>
              {isVirtualOk ? (
                <CheckCircle className="h-5 w-5 text-emerald-500" strokeWidth={1.5} />
              ) : (
                <AlertCircle className={`h-5 w-5 ${virtualStock > 0 ? 'text-orange-500' : 'text-red-500'}`} strokeWidth={1.5} />
              )}
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold font-mono ${
                isVirtualOk ? 'text-emerald-600' : 
                virtualStock > 0 ? 'text-orange-600' : 
                'text-red-600'
              }`}>
                {virtualStock}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {displayStock} {hasPendingOrders ? `+ ${pendingQuantity + cartQuantity} pendente` : ''}
              </p>
            </CardContent>
          </Card>

          {/* Ponto de Reencomenda */}
          <Card className="border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Ponto de Reencomenda</CardTitle>
              <TrendingDown className="h-5 w-5 text-orange-500" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-mono">
                {effectiveReorder ?? <span className="text-slate-400 text-2xl">—</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {effectiveReorder ? 'Nível mínimo de stock' : 'Não configurado'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Existências por Armazém */}
        {(() => {
          // Use stock_by_warehouse_all if available (for store users to see all warehouses)
          // Otherwise use stock_by_warehouse (for admins or when all is not available)
          const warehouseStock = product.stock_by_warehouse_all || product.stock_by_warehouse || {};
          const hasWarehouseData = Object.keys(warehouseStock).length > 0;
          const userWarehouse = product.stock_by_warehouse ? Object.keys(product.stock_by_warehouse)[0] : null;
          
          return hasWarehouseData && (
            <div className="lg:col-span-3">
              <Card className="border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Warehouse className="h-4 w-4 text-slate-500" />
                    Existências por Armazém
                  </CardTitle>
                  <span className="text-xs text-muted-foreground">
                    Total: <span className="font-mono font-semibold">{Object.values(warehouseStock).reduce((a, b) => a + (parseInt(b) || 0), 0)}</span>
                  </span>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                    {Object.entries(warehouseStock)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([warehouse, qty]) => {
                        const qtyNum = parseInt(qty) || 0;
                        const hasStock = qtyNum > 0;
                        const isUserWarehouse = userWarehouse === warehouse || (product.stock_by_warehouse && warehouse in product.stock_by_warehouse);
                        return (
                          <div
                            key={warehouse}
                            className={`flex flex-col items-center justify-center p-2 rounded border ${
                              isUserWarehouse && hasStock
                                ? 'border-emerald-300 bg-emerald-100 ring-1 ring-emerald-400'
                                : hasStock 
                                  ? 'border-emerald-200 bg-emerald-50/50' 
                                  : 'border-slate-200 bg-slate-50/50'
                            }`}
                            data-testid={`warehouse-stock-${warehouse}`}
                            title={isUserWarehouse ? 'Meu armazém' : `Armazém ${warehouse}`}
                          >
                            <span className={`text-[10px] font-medium uppercase tracking-wide ${isUserWarehouse ? 'text-emerald-700' : 'text-slate-500'}`}>{warehouse}</span>
                            <span className={`text-lg font-bold font-mono ${hasStock ? (isUserWarehouse ? 'text-emerald-800' : 'text-emerald-700') : 'text-slate-400'}`}>
                              {qtyNum}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* Stock Status Badge */}
        <div className="lg:col-span-3">
          {product.stock_status_label === 'ok' ? (
            <div className="flex items-center gap-3 p-4 rounded-sm border border-emerald-200 bg-emerald-50/50">
              <CheckCircle className="h-6 w-6 text-emerald-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-900">Stock em níveis saudáveis</p>
                <p className="text-sm text-emerald-700">Stock virtual ({virtualStock}) está acima do ponto de reencomenda ({effectiveReorder ?? '—'}).</p>
              </div>
            </div>
          ) : product.stock_status_label === 'pending_resolution' ? (
            <div className="flex items-center gap-3 p-4 rounded-sm border border-blue-200 bg-blue-50/50">
              <Truck className="h-6 w-6 text-blue-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-900">Encomendas pendentes</p>
                <p className="text-sm text-blue-700">
                  Este produto tem {pendingQuantity + cartQuantity} unidades pendentes. Quando recebidas, o stock virtual será {virtualStock}.
                </p>
              </div>
            </div>
          ) : isOutOfStock ? (
            <div className="flex items-center gap-3 p-4 rounded-sm border border-red-200 bg-red-50/50">
              <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-900">Produto sem stock!</p>
                <p className="text-sm text-red-700">Este produto precisa de reabastecimento imediato.</p>
              </div>
            </div>
          ) : isLowStock ? (
            <div className="flex items-center gap-3 p-4 rounded-sm border border-orange-200 bg-orange-50/50">
              <AlertCircle className="h-6 w-6 text-orange-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-orange-900">Aviso de stock baixo</p>
                <p className="text-sm text-orange-700">
                  Stock atual ({product.current_stock}) está abaixo do ponto de reencomenda ({effectiveReorder ?? '—'}).
                </p>
              </div>
            </div>
          ) : null}
        </div>

        {/* Ficha de Procurement (Phase 4) */}
        <FichaProcurement sku={product.sku} onPrices={setFichaPrices} />

        {/* Procurement Config - Only visible to users with procurement permissions */}
        {canEditProcurement && (
        <Card className="lg:col-span-3 border border-slate-200 rounded-sm shadow-none" data-testid="procurement-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-slate-600" />
              Procurement Intelligence
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              <strong>É aqui que defines os valores que a APP usa.</strong> Os overrides manuais
              sobrepõem-se sempre aos cálculos do AI. Este SKU só entra no cockpit (alertas,
              sugestões, forecasting) se "Acompanhar com AI" estiver activo E o fornecedor
              estiver activado em Procurement Intelligence.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Master gates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start justify-between gap-3 p-3 rounded border border-slate-200 bg-slate-50/50">
                <div>
                  <Label htmlFor="ai_tracking_enabled" className="text-sm font-medium">
                    Acompanhar com AI/procurement
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Gate principal. Sem isto, o SKU não gera cards/alertas/forecast.
                  </p>
                </div>
                <Switch
                  id="ai_tracking_enabled"
                  data-testid="switch-ai-tracking"
                  checked={!!procurementForm.ai_tracking_enabled}
                  onCheckedChange={(v) => setProcurementForm({ ...procurementForm, ai_tracking_enabled: v })}
                />
              </div>

              <div className="flex items-start justify-between gap-3 p-3 rounded border border-slate-200 bg-slate-50/50">
                <div>
                  <Label htmlFor="excluded_from_procurement" className="text-sm font-medium">
                    Excluir explicitamente de procurement
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Força exclusão mesmo se AI tracking estiver activo.
                  </p>
                </div>
                <Switch
                  id="excluded_from_procurement"
                  data-testid="switch-excluded"
                  checked={!!procurementForm.excluded_from_procurement}
                  onCheckedChange={(v) => setProcurementForm({ ...procurementForm, excluded_from_procurement: v })}
                />
              </div>
            </div>

            {/* Sub-toggles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'allow_alerts', label: 'Permitir alertas' },
                { key: 'allow_forecast', label: 'Permitir forecast' },
                { key: 'on_demand', label: 'Sob encomenda' },
                { key: 'seasonal', label: 'Sazonal' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <Switch
                    id={key}
                    data-testid={`switch-${key}`}
                    checked={!!procurementForm[key]}
                    onCheckedChange={(v) => setProcurementForm({ ...procurementForm, [key]: v })}
                  />
                  <Label htmlFor={key} className="text-sm">{label}</Label>
                </div>
              ))}
            </div>

            {/* Priority + preferred supplier */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="operational_priority" className="text-xs">Prioridade operacional</Label>
                <Select
                  value={procurementForm.operational_priority || ''}
                  onValueChange={(v) => setProcurementForm({ ...procurementForm, operational_priority: v })}
                >
                  <SelectTrigger data-testid="select-operational-priority">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Crítico</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="low">Baixo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="preferred_supplier_code" className="text-xs">
                  Código fornecedor preferencial (SAGE)
                </Label>
                <Input
                  id="preferred_supplier_code"
                  data-testid="input-preferred-supplier"
                  placeholder={product.sage_supplier_code || '—'}
                  value={procurementForm.preferred_supplier_code}
                  onChange={(e) => setProcurementForm({ ...procurementForm, preferred_supplier_code: e.target.value })}
                />
              </div>
            </div>

            {/* Manual overrides */}
            <div>
              <p className="text-xs text-slate-500 mb-2">
                Overrides manuais (deixar vazio = usar resolução product-supplier → supplier → none).
                Nunca usar defaults artificiais.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Reorder point manual</Label>
                  <Input
                    data-testid="input-manual-reorder-point"
                    type="number"
                    value={procurementForm.manual_reorder_point}
                    onChange={(e) => setProcurementForm({ ...procurementForm, manual_reorder_point: e.target.value })}
                    placeholder="—"
                  />
                </div>
                <div>
                  <Label className="text-xs">Stock máximo</Label>
                  <Input
                    data-testid="input-manual-max-stock"
                    type="number"
                    value={procurementForm.manual_max_stock}
                    onChange={(e) => setProcurementForm({ ...procurementForm, manual_max_stock: e.target.value })}
                    placeholder="—"
                  />
                </div>
                <div>
                  <Label className="text-xs">Safety stock</Label>
                  <Input
                    data-testid="input-manual-safety-stock"
                    type="number"
                    value={procurementForm.manual_safety_stock}
                    onChange={(e) => setProcurementForm({ ...procurementForm, manual_safety_stock: e.target.value })}
                    placeholder="—"
                  />
                </div>
                <div>
                  <Label className="text-xs">MOQ manual</Label>
                  <Input
                    data-testid="input-manual-moq"
                    type="number"
                    value={procurementForm.manual_moq}
                    onChange={(e) => setProcurementForm({ ...procurementForm, manual_moq: e.target.value })}
                    placeholder="—"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="procurement_notes" className="text-xs">Notas procurement</Label>
              <Textarea
                id="procurement_notes"
                data-testid="input-procurement-notes"
                rows={2}
                value={procurementForm.procurement_notes}
                onChange={(e) => setProcurementForm({ ...procurementForm, procurement_notes: e.target.value })}
                placeholder="Ex: contactar comercial X antes de encomenda > 100un"
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveProcurement}
                disabled={savingProcurement}
                data-testid="save-procurement-config"
              >
                {savingProcurement && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar configuração procurement
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Informação do Produto — espelho do SAGE (só leitura) */}
        <Card className="lg:col-span-2 border border-slate-200 rounded-sm shadow-none">
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
              <span>Informação do Produto</span>
              <Badge variant="outline" className="text-[10px] text-slate-500 font-normal">
                Sincronizado do SAGE — só leitura
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <PriceStrip prices={fichaPrices} />

            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground mb-0.5">Nome</p>
                <p className="font-medium" data-testid="info-product-name">{product.name || '—'}</p>
              </div>
              {product.description && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground mb-0.5">Descrição</p>
                  <p>{product.description}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Categoria</p>
                <p data-testid="info-product-category">{product.category || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Código de Barras (EAN)</p>
                <p className="font-mono" data-testid="info-product-ean">{product.ean || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Fornecedor</p>
                <p data-testid="info-product-supplier">
                  {product.supplier || '—'}
                  {product.sage_supplier_code && (
                    <span className="font-mono text-xs text-slate-400 ml-2">({product.sage_supplier_code})</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Stock Atual (SAGE)</p>
                <p className="font-mono font-semibold" data-testid="info-product-stock">{product.current_stock}</p>
              </div>
            </div>

            <p className="text-xs text-slate-400 border-t pt-3">
              Estes dados vêm do SAGE a cada sincronização. Para alterar preços, nome ou stock,
              altera no SAGE. Os parâmetros que a app usa definem-se em "Procurement Intelligence".
            </p>
          </CardContent>
        </Card>

        {/* Product Details */}
        <Card className="border border-slate-200 rounded-sm shadow-none">
          <CardHeader>
            <CardTitle>Detalhes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">SKU</p>
              <p className="font-mono text-sm font-medium">{product.sku}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Última Sincronização</p>
              <p className="text-sm">
                {product.last_synced
                  ? new Date(product.last_synced).toLocaleString()
                  : 'Nunca'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Criado em</p>
              <p className="text-sm">{new Date(product.created_at).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Última Atualização</p>
              <p className="text-sm">{new Date(product.updated_at).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductDetail;
